import Head from 'next/head'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  Box,
  Button,
  Card,
  Container,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Layout as AuthLayout } from 'layouts/Auth/layout'
import { useUserContext } from 'hooks'
import { login } from 'api/requests/auth'
import { TokenStorage } from 'utils/auth'
import { getCurrentUserInfo } from 'api/requests/user'
import { NextPageWithLayout } from 'next'
import { Logo } from 'components/Logo'
import { LoadingButton } from '@mui/lab'
import { setCookie } from 'utils/cookie'
import { USER } from 'constants/cookieKeys'
import { GoogleIcon } from 'components/CustomIcons/GoogleIcon'
import { getAnonymousAuthState } from 'api/requests/authState/getAnonymousAuthState'
import { googleAccountScopes } from 'utils/auth/google'
import { toast } from 'react-toastify'

const Login: NextPageWithLayout = () => {
  const { setUser } = useUserContext()

  const router = useRouter()

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      submit: null,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Must be a valid email')
        .max(255)
        .required('Email is required'),
      password: Yup.string().max(255).required('Password is required'),
    }),
    onSubmit: async (values, helpers) => {
      try {
        const response = await login(values)
        if (response.data) {
          const { accessToken, refreshToken } = response.data
          const tokenStorage = TokenStorage.getInstance()
          tokenStorage.setAccessToken(accessToken)
          tokenStorage.setRefreshToken(refreshToken)

          try {
            const result = await getCurrentUserInfo()
            const user = result.data
            if (!user) {
              return
            }
            setCookie(USER, JSON.stringify(user))
            setUser(user)
            void router.push('/agent')
          } catch (error) {
            console.error(error)
          }
        }
      } catch (err: any) {
        helpers.setStatus({ success: false })
        helpers.setSubmitting(false)
      }
    },
  })

  const handleGoogleSignUp = async () => {
    try {
      const response = await getAnonymousAuthState()
      if (response.data && response.data.token) {
        const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID
        const redirectUri = process.env.NEXT_PUBLIC_GOOGLE_AUTH_REDIRECT_URI

        const scopes = googleAccountScopes.join(' ')
        const responseType = 'code'
        const state = response.data.token

        const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${encodeURIComponent(
          scopes
        )}&response_type=${responseType}&state=${state}&access_type=offline&prompt=consent`
        window.location.href = oauthUrl
      }
    } catch (error) {
      toast.error('Failed to connect to Google')
    }
  }

  return (
    <>
      <Head>
        <title>Login</title>
        <meta
          name='description'
          content='Login to access your account on Insight AI'
        ></meta>
        <meta name='robots' content='noindex, nofollow'></meta>
      </Head>

      <Box
        component='main'
        sx={{
          flex: '1 1 auto',
          flexDirection: 'column',
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Logo></Logo>
        </Box>
        <Container maxWidth='xs' sx={{ minWidth: '400px' }}>
          <Card sx={{ minWidth: '400px' }}>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: 4,
              }}
            >
              <Stack spacing={1} sx={{ mb: 3 }}>
                <Typography variant='h5' sx={{ textAlign: 'center' }}>
                  Welcome back!
                </Typography>
                <Typography color='text.secondary' variant='body2'>
                  Don&apos;t have an account? &nbsp;
                  <NextLink href={'/register'}>Register</NextLink>
                </Typography>
              </Stack>

              {/* Google Sign Up Button */}
              <Box sx={{ width: '100%', mb: 3 }}>
                <Button
                  fullWidth
                  size='large'
                  variant='outlined'
                  onClick={handleGoogleSignUp}
                  sx={{
                    borderColor: '#dadce0',
                    color: '#3c4043',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '14px',
                    py: 1.5,
                    '&:hover': {
                      borderColor: '#dadce0',
                      backgroundColor: '#f8f9fa',
                    },
                  }}
                  startIcon={<GoogleIcon />}
                >
                  Continue with Google
                </Button>
              </Box>

              {/* Divider */}
              <Box
                sx={{
                  width: '100%',
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{ flex: 1, height: '1px', backgroundColor: '#dadce0' }}
                />
                <Typography sx={{ px: 2, color: '#5f6368', fontSize: '14px' }}>
                  or
                </Typography>
                <Box
                  sx={{ flex: 1, height: '1px', backgroundColor: '#dadce0' }}
                />
              </Box>

              <form
                noValidate
                onSubmit={formik.handleSubmit}
                style={{ width: '100%' }}
              >
                <Stack spacing={3} sx={{ width: '100%' }}>
                  <TextField
                    error={!!(formik.touched.email && formik.errors.email)}
                    fullWidth
                    helperText={formik.touched.email && formik.errors.email}
                    label='Email Address'
                    name='email'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type='email'
                    value={formik.values.email}
                  />
                  <TextField
                    error={
                      !!(formik.touched.password && formik.errors.password)
                    }
                    fullWidth
                    helperText={
                      formik.touched.password && formik.errors.password
                    }
                    label='Password'
                    name='password'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type='password'
                    value={formik.values.password}
                  />{' '}
                  <FormHelperText sx={{ mt: 0, fontSize: '13px' }}>
                    Forgot your password? &nbsp;
                    <NextLink href={'/reset-password'}>Reset Password</NextLink>
                  </FormHelperText>
                </Stack>
                {formik.errors.submit && (
                  <Typography color='error' sx={{ mt: 3 }} variant='body2'>
                    {formik.errors.submit}
                  </Typography>
                )}
                <LoadingButton
                  fullWidth
                  loading={formik.isSubmitting}
                  size='large'
                  sx={{ mt: 3 }}
                  disabled={formik.isSubmitting}
                  type='submit'
                  variant='contained'
                >
                  Continue
                </LoadingButton>
              </form>
            </Box>
          </Card>
        </Container>
      </Box>
    </>
  )
}

Login.getLayout = page => <AuthLayout>{page}</AuthLayout>

export default Login
